import { useState, useCallback } from "react";
import {
  Apis,
  commercialDesignProjectApis,
  corporateDesignProjectApis,
} from "../../lib/Apis";

const getApiError = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

// --- Hook from ConstructionCalculator/useHook.js ---
export const useConstructionCalculatorHook = () => {
  const addConstructionCalculator = async (data) => {
    try {
      const response = await Apis.addConstructionCalculator(data);
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to add construction calculator.");
      }
      return body;
    } catch (error) {
      throw new Error(getApiError(error, "Failed to add construction calculator."));
    }
  };

  const getConstructionCalculators = async (setCalculators, setPagination, offset, limit) => {
    try {
      const response = await Apis.getConstructionCalculators(offset, limit);
      const payload = response?.data?.data ?? response?.data;
      const list =
        payload?.calculators ||
        (Array.isArray(payload?.data) ? payload.data : []) ||
        (Array.isArray(payload) ? payload : []);
      const page = payload?.pagination ?? null;

      if (setCalculators) setCalculators(list);
      if (setPagination && page) {
        setPagination((prev) => ({
          ...prev,
          ...page,
          offset,
          limit,
        }));
      }
      return payload;
    } catch (error) {
      throw new Error(getApiError(error, "Failed to fetch construction calculators."));
    }
  };

  const updateConstructionCalculator = async (id, data) => {
    try {
      const response = await Apis.updateConstructionCalculator(id, data);
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to update construction calculator.");
      }
      return body;
    } catch (error) {
      throw new Error(getApiError(error, "Failed to update construction calculator."));
    }
  };

  const deleteConstructionCalculator = async (id) => {
    try {
      const response = await Apis.deleteConstructionCalculator(id);
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to delete construction calculator.");
      }
      return body;
    } catch (error) {
      throw new Error(getApiError(error, "Failed to delete construction calculator."));
    }
  };

  return {
    addConstructionCalculator,
    getConstructionCalculators,
    updateConstructionCalculator,
    deleteConstructionCalculator,
  };
};

const createUseProjects = (api) => () => {
    const getProjectsData = async (setOurProjectsData, id, paginationParams = {}) => {
        try {
            const response = await api.getOnTheBasisOfSubCategory(id, paginationParams);
            const payload = response?.data?.data ?? response?.data ?? {};
            const list =
                payload?.projects ||
                (Array.isArray(payload?.data) ? payload.data : []) ||
                (Array.isArray(payload) ? payload : []);
            setOurProjectsData(list);
            return payload?.pagination ?? null;
        } catch (error) {
            console.log(error);
            throw new Error(getApiError(error, "Failed to fetch projects."));
        }
    };

    const addProject = async (data) => {
        try {
            const response = await api.addProject(data);
            const body = response?.data;
            if (body?.success === false) {
                throw new Error(body?.message || "Failed to add project.");
            }
            return body;
        } catch (error) {
            console.log(error);
            throw new Error(getApiError(error, "Failed to add project."));
        }
    };

    const updateProject = async (id, data) => {
        try {
            const response = await api.updateProject(id, data);
            const body = response?.data;
            if (body?.success === false) {
                throw new Error(body?.message || "Failed to update project.");
            }
            return body;
        } catch (error) {
            console.log(error);
            throw new Error(getApiError(error, "Failed to update project."));
        }
    };

    const deleteProject = async (id) => {
        try {
            const response = await api.deleteProject(id);
            const body = response?.data;
            if (body?.success === false) {
                throw new Error(body?.message || "Failed to delete project.");
            }
            return body;
        } catch (error) {
            console.log(error);
            throw new Error(getApiError(error, "Failed to delete project."));
        }
    };

    const getProjectById = async (id) => {
        try {
            const response = await api.getProjectById(id);
            return response?.data?.data ?? response?.data;
        } catch (error) {
            console.log(error);
            throw new Error(getApiError(error, "Failed to fetch project."));
        }
    };

    const getAllProjectCategories = async (setProjectCategories) => {
        try {
            const response = await api.getAllProjectCategories();
            const payload = response?.data?.data ?? response?.data ?? {};
            const list =
                payload?.projectCategories ||
                (Array.isArray(payload) ? payload : []);
            setProjectCategories(list);
            return list;
        } catch (error) {
            console.log(error);
            throw new Error(getApiError(error, "Failed to fetch project categories."));
        }
    };

    const getProjectByIdData = async (id, setProjectData) => {
        try {
            const response = await api.getProjectById(id);
            const payload = response?.data?.data ?? response?.data ?? {};
            const projects = payload?.projects || [];
            setProjectData(projects[0] || null);
            return projects[0] || null;
        } catch (error) {
            console.log(error);
            throw new Error(getApiError(error, "Failed to fetch project details."));
        }
    };

    const getSimilarProjectExpectAdded = async (id, setExpectAdded) => {
        try {
            const response = await api.getSimilarProjectExpectAdded(id);
            const payload = response?.data?.data ?? response?.data ?? {};
            const list =
                payload?.projects ||
                (Array.isArray(payload) ? payload : []);
            setExpectAdded(list);
            return list;
        } catch (error) {
            console.log(error);
            throw new Error(getApiError(error, "Failed to fetch similar projects."));
        }
    };

    const postSimilarProject = async (data) => {
        try {
            const response = await api.postSimilarProject(data);
            const body = response?.data;
            if (body?.success === false) {
                throw new Error(body?.message || "Failed to add similar project.");
            }
            return body;
        } catch (error) {
            console.log(error);
            throw new Error(getApiError(error, "Failed to add similar project."));
        }
    };

    const getAddedSimilarProject = async (id, setAddedSimilarProject) => {
        try {
            const response = await api.getAddedSimilarProject(id);
            const payload = response?.data?.data ?? response?.data ?? {};
            const list =
                payload?.similarProjects ||
                (Array.isArray(payload) ? payload : []);
            setAddedSimilarProject(list);
            return list;
        } catch (error) {
            console.log(error);
            throw new Error(getApiError(error, "Failed to fetch added similar projects."));
        }
    };

    const deleteSimilarProject = async (id) => {
        try {
            const response = await api.deleteSimilarProject(id);
            const body = response?.data;
            if (body?.success === false) {
                throw new Error(body?.message || "Failed to delete similar project.");
            }
            return body;
        } catch (error) {
            console.log(error);
            throw new Error(getApiError(error, "Failed to delete similar project."));
        }
    };

    const updateSimilarProjectPosition = async (id, position) => {
        try {
            const response = await api.updateSimilarProjectPosition(id, position);
            const body = response?.data;
            if (body?.success === false) {
                throw new Error(body?.message || "Failed to update similar project position.");
            }
            return body;
        } catch (error) {
            console.log(error);
            throw new Error(getApiError(error, "Failed to update similar project position."));
        }
    };

    const getParentProjectCategories = async (setParentProjectCategories) => {
        try {
            const response = await api.getParentProjectCategories();
            const payload = response?.data?.data ?? response?.data ?? [];
            const list = Array.isArray(payload)
                ? payload
                : payload?.categories || [];
            setParentProjectCategories(list);
            return list;
        } catch (error) {
            console.log(error);
            throw new Error(getApiError(error, "Failed to fetch parent categories."));
        }
    };

    const addSubCategory = async (data) => {
        try {
            const response = await api.addSubCategory(data);
            const body = response?.data;
            if (body?.success === false) {
                throw new Error(body?.message || "Failed to add sub category.");
            }
            return body;
        } catch (error) {
            console.log(error);
            throw new Error(getApiError(error, "Failed to add sub category."));
        }
    };

    const updateSubCategory = async (id, data) => {
        try {
            let response;
            if (data instanceof FormData) {
                data.append("id", id);
                response = await api.updateSubCategory(data);
            } else {
                response = await api.updateSubCategory({ id, ...data });
            }
            const body = response?.data;
            if (body?.success === false) {
                throw new Error(body?.message || "Failed to update sub category.");
            }
            return body;
        } catch (error) {
            console.log(error);
            throw new Error(getApiError(error, "Failed to update sub category."));
        }
    };

    const getSubCategories = async (setSubCategories, paginationParams = {}) => {
        try {
            const response = await api.getSubCategories(paginationParams);
            const payload = response?.data?.data ?? response?.data ?? {};
            const list =
                payload?.categories ||
                (Array.isArray(payload?.data) ? payload.data : []) ||
                (Array.isArray(payload) ? payload : []);
            setSubCategories(list);
            return payload?.pagination ?? null;
        } catch (error) {
            console.log(error);
            throw new Error(getApiError(error, "Failed to fetch sub categories."));
        }
    };

    const toggleProjectStatus = async (id) => {
        try {
            const response = await api.toggleProjectStatus(id);
            const body = response?.data;
            if (body?.success === false) {
                throw new Error(body?.message || "Failed to toggle project status.");
            }
            return body;
        } catch (error) {
            console.log(error);
            throw new Error(getApiError(error, "Failed to toggle project status."));
        }
    };

    const showProjectOnHomeScreen = async (projectId, homeScreen) => {
        try {
            const response = await api.addProjectToHomeScreen({
                projectId,
                homeScreen: homeScreen ? 1 : 0,
            });
            const body = response?.data;
            if (body?.success === false) {
                throw new Error(body?.message || "Failed to update home screen status.");
            }
            return body;
        } catch (error) {
            console.error("Error updating home screen status:", error);
            throw new Error(getApiError(error, "Failed to update home screen status."));
        }
    };

    const updateSubCategoryPosition = async (id, position) => {
        try {
            const response = await api.updateSubCategoryPosition(id, position);
            const body = response?.data;
            if (body?.success === false) {
                throw new Error(body?.message || "Failed to update category position.");
            }
            return body;
        } catch (error) {
            console.log(error);
            throw new Error(getApiError(error, "Failed to update category position."));
        }
    };

    const updateProjectPosition = async (projectId, newPosition) => {
        try {
            const response = await api.updateProjectPosition({ projectId, newPosition });
            const body = response?.data;
            if (body?.success === false) {
                throw new Error(body?.message || "Failed to update project position.");
            }
            return body;
        } catch (error) {
            console.error("Error updating project position:", error);
            throw new Error(getApiError(error, "Failed to update project position."));
        }
    };

    return {
        getProjectsData,
        addProject,
        updateProject,
        deleteProject,
        getProjectById,
        getAllProjectCategories,
        getProjectByIdData,
        getSimilarProjectExpectAdded,
        postSimilarProject,
        getAddedSimilarProject,
        deleteSimilarProject,
        updateSimilarProjectPosition,
        getParentProjectCategories,
        addSubCategory,
        updateSubCategory,
        getSubCategories,
        toggleProjectStatus,
        showProjectOnHomeScreen,
        updateSubCategoryPosition,
        updateProjectPosition,
    };
};


export const useProjects = createUseProjects({
    getOnTheBasisOfSubCategory: Apis.getOnTheBasisOfSubCategory,
    addProject: Apis.addProject,
    updateProject: Apis.updateProject,
    deleteProject: Apis.deleteProject,
    getProjectById: Apis.getProjectById,
    getAllProjectCategories: Apis.getAllProjectCategories,
    getSimilarProjectExpectAdded: Apis.getSimilarProjectExpectAdded,
    postSimilarProject: Apis.postSimilarProject,
    getAddedSimilarProject: Apis.getAddedSimilarProject,
    deleteSimilarProject: Apis.deleteSimilarProject,
    updateSimilarProjectPosition: Apis.updateSimilarProjectPosition,
    getParentProjectCategories: Apis.getParentProjectCategories,
    addSubCategory: Apis.addSubCategory,
    updateSubCategory: Apis.updateSubCategory,
    getSubCategories: Apis.getSubCategories,
    toggleProjectStatus: Apis.toggleProjectStatus,
    addProjectToHomeScreen: Apis.addProjectToHomeScreen,
    updateSubCategoryPosition: Apis.updateSubCategoryPosition,
    updateProjectPosition: Apis.updateProjectPosition,
});
export const useCommercialDesignProjects = createUseProjects(commercialDesignProjectApis);
export const useCorporateDesignProjects = createUseProjects(corporateDesignProjectApis);

// --- Hook from Services/useHook.js ---
export const useServices = () => {
  const [loading, setLoading] = useState(false);

  const unwrapServicesPayload = (body) => {
    const payload = body?.data ?? body ?? {};
    const services =
      payload?.services ||
      (Array.isArray(payload?.data) ? payload.data : []) ||
      (Array.isArray(payload) ? payload : []);
    const pagination = payload?.pagination ?? null;
    const totalCount =
      payload?.totalCount ??
      pagination?.totalRecords ??
      services.length ??
      0;
    return { payload, services, pagination, totalCount };
  };

  const unwrapCategoriesPayload = (body) => {
    const payload = body?.data ?? body ?? {};
    const categories =
      payload?.categories ||
      (Array.isArray(payload?.data) ? payload.data : []) ||
      (Array.isArray(payload) ? payload : []);
    const pagination = payload?.pagination ?? null;
    const totalCount =
      payload?.totalCount ??
      pagination?.totalRecords ??
      categories.length ??
      0;
    return { payload, categories, pagination, totalCount };
  };

  const getServices = useCallback(async (
    setServiceData,
    setTotal,
    offset = 0,
    limit = 10,
    searchFilter = ""
  ) => {
    try {
      const params = {};
      if (searchFilter && searchFilter.trim()) {
        params.searchFilter = searchFilter.trim();
      }
      const response = await Apis.getServices(offset, limit, params);
      const { services, pagination, totalCount, payload } = unwrapServicesPayload(
        response?.data
      );
      setServiceData(services);
      setTotal(totalCount);
      return { data: services, pagination, totalCount, ...payload };
    } catch (error) {
      console.error("Error fetching services:", error);
      throw new Error(getApiError(error, "Failed to fetch services."));
    }
  }, []);

  const searchServices = useCallback(async (searchFilter, offset = 0, limit = 10) => {
    try {
      const params = {};
      if (searchFilter && searchFilter.trim()) {
        params.searchFilter = searchFilter.trim();
      }
      const response = await Apis.getServices(offset, limit, params);
      const { services, pagination, totalCount, payload } = unwrapServicesPayload(
        response?.data
      );
      return { data: services, pagination, totalCount, ...payload };
    } catch (error) {
      console.error("Error searching services:", error);
      throw new Error(getApiError(error, "Failed to search services."));
    }
  }, []);

  const addService = useCallback(async (data) => {
    try {
      const response = await Apis.addService(data);
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to add service.");
      }
      return body;
    } catch (error) {
      console.error("Error adding service:", error);
      throw new Error(getApiError(error, "Failed to add service."));
    }
  }, []);

  const updateService = useCallback(async (id, data) => {
    try {
      const response = await Apis.updateService(id, data);
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to update service.");
      }
      return body;
    } catch (error) {
      console.error("Error updating service:", error);
      throw new Error(getApiError(error, "Failed to update service."));
    }
  }, []);

  const deleteService = useCallback(async (id) => {
    try {
      const response = await Apis.deleteService(id);
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to delete service.");
      }
      return body;
    } catch (error) {
      console.error("Error deleting service:", error);
      throw new Error(getApiError(error, "Failed to delete service."));
    }
  }, []);

  const getServiceById = useCallback(async (id, setServiceData) => {
    try {
      const response = await Apis.getServiceById(id);
      const payload = response?.data?.data ?? response?.data;
      const service = payload?.service ?? payload;
      if (setServiceData) setServiceData(service);
      return service;
    } catch (error) {
      console.error("Error fetching service by id:", error);
      throw new Error(getApiError(error, "Failed to fetch service."));
    }
  }, []);

  const addServiceCategory = useCallback(async (data) => {
    try {
      setLoading(true);
      const response = await Apis.addServiceCategory(data);
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to add service category.");
      }
      return body;
    } catch (error) {
      throw new Error(getApiError(error, "Failed to add service category."));
    } finally {
      setLoading(false);
    }
  }, []);

  const updateServiceCategory = useCallback(async (id, data) => {
    try {
      setLoading(true);
      const response = await Apis.updateServiceCategory(id, data);
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to update service category.");
      }
      return body;
    } catch (error) {
      throw new Error(getApiError(error, "Failed to update service category."));
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteServiceCategory = useCallback(async (id) => {
    try {
      setLoading(true);
      const response = await Apis.deleteServiceCategory(id);
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to delete service category.");
      }
      return body;
    } catch (error) {
      throw new Error(getApiError(error, "Failed to delete service category."));
    } finally {
      setLoading(false);
    }
  }, []);

  const getServicesCategories = useCallback(async (
    setCategories,
    setTotal,
    offset = 0,
    limit = 10,
    name = "",
  ) => {
    try {
      setLoading(true);
      const params = {};
      if (name && name.trim()) params.name = name.trim();

      const response = await Apis.getServicesCategories(offset, limit, params);
      const { categories, totalCount, pagination, payload } =
        unwrapCategoriesPayload(response?.data);
      setCategories(categories);
      setTotal(totalCount);
      return { data: categories, totalCount, pagination, ...payload };
    } catch (error) {
      throw new Error(getApiError(error, "Failed to fetch services categories."));
    } finally {
      setLoading(false);
    }
  }, []);

  const searchServicesCategories = useCallback(async (name, offset = 0, limit = 10) => {
    try {
      const params = {};
      if (name && name.trim()) params.name = name.trim();

      const response = await Apis.getServicesCategories(offset, limit, params);
      const { categories, totalCount, pagination, payload } =
        unwrapCategoriesPayload(response?.data);
      return { data: categories, totalCount, pagination, ...payload };
    } catch (error) {
      throw new Error(getApiError(error, "Failed to search services categories."));
    }
  }, []);

  const getServiceCategoriesForSelect = useCallback(async (setCategories) => {
    try {
      const response = await Apis.getServicesCategoriesForSelect();
      const { categories } = unwrapCategoriesPayload(response?.data);
      if (setCategories && typeof setCategories === "function") {
        setCategories(categories);
      }
      return categories;
    } catch (error) {
      console.error("Error fetching service categories:", error);
      throw new Error(getApiError(error, "Failed to fetch service categories."));
    }
  }, []);

  return {
    loading,
    getServices,
    searchServices,
    addService,
    updateService,
    deleteService,
    getServiceById,
    addServiceCategory,
    updateServiceCategory,
    deleteServiceCategory,
    getServicesCategories,
    searchServicesCategories,
    getServiceCategoriesForSelect,
  };
};
